import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "@fg/icons";
import { cn } from "@/lib/utils";
import { cssFamily, loadLocalFonts, localFamilies, localFontsSupported, onLocalFonts, ONLINE_FONTS, showFont, STANDARD_FONTS } from "./fonts";

interface Row { family: string; group: string }

/**
 * The font box of the text bar. The list always opens with every font, the current one marked; it narrows only while the person
 * is typing, so choosing a font never hides the others.
 */
export function FontPicker({ value, onPick, className }: { value: string; onPick(family: string): void; className?: string }) {
    const [open, setOpen] = useState(false);
    /** What the person has typed since opening; null means "not typing", so the list is complete. */
    const [query, setQuery] = useState<string | null>(null);
    const [active, setActive] = useState(0);
    const [local, setLocal] = useState<string[]>(localFamilies);
    const [note, setNote] = useState<string | null>(null);
    const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null);
    const input = useRef<HTMLInputElement>(null);
    const list = useRef<HTMLDivElement>(null);

    useEffect(() => onLocalFonts(() => setLocal(localFamilies())), []);

    const rows = useMemo<Row[]>(() => {
        const all: Row[] = [
            ...STANDARD_FONTS.map((f) => ({ family: f, group: "Standard" })),
            ...(["Sans", "Serif", "Mono", "Display", "Handwriting"] as const).flatMap((k) => ONLINE_FONTS.filter((f) => f.kind === k).map((f) => ({ family: f.family, group: `${k} · online, open licence` }))),
            ...local.map((f) => ({ family: f, group: "On this computer" }))
        ];
        const q = query?.trim().toLowerCase();
        return q ? all.filter((r) => r.family.toLowerCase().includes(q)) : all;
    }, [local, query]);

    const show = () => {
        const r = input.current?.getBoundingClientRect();
        if (r) setBox({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 240) });
        setQuery(null);
        setNote(null);
        setOpen(true);
        const at = rows.findIndex((x) => x.family === value);
        setActive(Math.max(0, at));
        window.requestAnimationFrame(() => input.current?.select());
    };
    const close = () => { setOpen(false); setQuery(null); };
    const pick = (family: string) => { close(); if (family !== value) onPick(family); };

    // keep the highlighted row in view, and load its face so the row previews itself
    useEffect(() => {
        if (!open) return;
        const row = rows[active];
        if (row) void showFont(row.family);
        list.current?.querySelector<HTMLElement>(`[data-row="${active}"]`)?.scrollIntoView({ block: "nearest" });
    }, [open, active, rows]);

    // the first time it opens, scroll to the current font
    useEffect(() => {
        if (open && query === null) list.current?.querySelector<HTMLElement>("[data-current=true]")?.scrollIntoView({ block: "center" });
    }, [open, query]);

    useEffect(() => {
        if (!open) return;
        const away = (e: PointerEvent) => {
            const t = e.target as Node;
            if (!list.current?.contains(t) && !input.current?.contains(t)) close();
        };
        window.addEventListener("pointerdown", away, true);
        return () => window.removeEventListener("pointerdown", away, true);
    }, [open]);

    let lastGroup = "";
    return (
        <>
            <div className={cn("relative", className)}>
                <input
                    ref={input}
                    role="combobox"
                    aria-label="Font"
                    aria-expanded={open}
                    aria-controls="fg-font-list"
                    autoComplete="off"
                    spellCheck={false}
                    value={query ?? value}
                    style={{ fontFamily: query === null ? cssFamily(value) : undefined }}
                    className="h-7 w-full rounded border bg-background pl-1.5 pr-6 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onFocus={show}
                    onClick={() => { if (!open) show(); }}
                    onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true); }}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "ArrowDown") { e.preventDefault(); if (!open) show(); else setActive((a) => Math.min(rows.length - 1, a + 1)); }
                        else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
                        else if (e.key === "Enter") { e.preventDefault(); const r = rows[active]; if (open && r) pick(r.family); }
                        else if (e.key === "Escape" || e.key === "Tab") close();
                    }}
                />
                <ChevronDown aria-hidden className="pointer-events-none absolute right-1.5 top-2 h-3 w-3 opacity-60" />
            </div>
            {open && box
                ? createPortal(
                    <div
                        ref={list}
                        id="fg-font-list"
                        role="listbox"
                        aria-label="Fonts"
                        style={{ left: box.left, top: box.top, width: box.width }}
                        className="fixed z-[60] max-h-72 overflow-auto rounded-md border bg-card py-1 text-xs text-card-foreground shadow-lg"
                    >
                        {rows.length === 0 ? <div className="px-3 py-2 text-muted-foreground">No font called “{query}”.</div> : null}
                        {rows.map((r, i) => {
                            const head = r.group !== lastGroup ? r.group : null;
                            lastGroup = r.group;
                            return (
                                <div key={`${r.group}|${r.family}`}>
                                    {head ? <div className="px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{head}</div> : null}
                                    <div
                                        role="option"
                                        aria-selected={r.family === value}
                                        data-row={i}
                                        data-current={r.family === value}
                                        className={cn("flex cursor-pointer items-center justify-between px-3 py-1 text-[13px]", i === active && "bg-accent", r.family === value && "font-semibold")}
                                        style={{ fontFamily: cssFamily(r.family) }}
                                        onPointerMove={() => setActive(i)}
                                        onPointerDown={(e) => { e.preventDefault(); pick(r.family); }}
                                    >
                                        <span>{r.family}</span>
                                        {r.family === value ? <span aria-hidden className="text-primary">✓</span> : null}
                                    </div>
                                </div>
                            );
                        })}
                        {localFontsSupported() && local.length === 0 && !query ? (
                            <button type="button" className="mt-1 block w-full border-t px-3 py-1.5 text-left text-primary hover:bg-accent" onPointerDown={(e) => { e.preventDefault(); loadLocalFonts().catch((err: Error) => setNote(err.message)); }}>
                                Show fonts installed on this computer…
                            </button>
                        ) : null}
                        {note ? <div role="alert" className="px-3 py-1 text-destructive">{note}</div> : null}
                    </div>,
                    document.body
                )
                : null}
        </>
    );
}
